/**
 * 统一 API 请求封装
 * - 基于浏览器原生 fetch
 * - 统一 baseURL（按环境切换）
 * - 强制 response.ok 校验，HTTP 错误不再被当作成功处理
 * - AbortController 超时控制
 * - 参数 encodeURIComponent 编码
 * - 统一错误类型与处理
 */

// baseURL 由环境变量注入：
//   开发环境 -> http://localhost:8201
//   上线环境 -> https://mingqicaiwu.com:8201
const BASE_URL = process.env.API_BASE_URL || ''

// 请求超时时间（毫秒）
const TIMEOUT = 10000

/**
 * 统一错误类型
 */
export class ApiError extends Error {
  constructor(status, message, data) {
    super(message || `请求失败 (HTTP ${status})`)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * 将对象序列化为 application/x-www-form-urlencoded
 * 所有值均经过 encodeURIComponent 处理
 * @param {Object} params
 * @returns {string}
 */
export function toFormData(params = {}) {
  return Object.keys(params)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&')
}

/**
 * 路径参数编码，防止非法字符注入
 * @param {*} value
 * @returns {string}
 */
export function encodePath(value) {
  return encodeURIComponent(value)
}

/**
 * 安全解析响应体
 */
async function safeBody(response) {
  const type = response.headers.get('content-type') || ''
  try {
    return type.indexOf('application/json') !== -1
      ? await response.json()
      : await response.text()
  } catch (e) {
    return null
  }
}

/**
 * 统一错误处理
 * 此处可扩展：埋点上报、统一 Toast 提示等
 */
function handleError(error) {
  if (error instanceof ApiError) {
    // 业务层已知错误，原样抛出由调用方处理
    throw error
  }
  if (error && error.name === 'AbortError') {
    throw new ApiError(0, '请求超时，请稍后重试')
  }
  // 网络层错误（断网、DNS 失败等）
  throw new ApiError(0, '网络异常，请检查网络连接')
}

/**
 * 核心请求方法
 * @param {string} url 接口路径（不含 baseURL）
 * @param {Object} options fetch 配置
 * @param {boolean} options.raw 为 true 时返回原始 Response 对象
 * @returns {Promise<any>}
 */
export async function request(url, options = {}) {
  const { raw = false, headers = {}, ...rest } = options

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT)

  try {
    const response = await fetch(BASE_URL + url, {
      ...rest,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...headers,
      },
      signal: controller.signal,
    })

    // 关键：fetch 不会因 4xx/5xx 进入 catch，必须显式校验
    if (!response.ok) {
      const body = await safeBody(response)
      throw new ApiError(response.status, `请求失败 (HTTP ${response.status})`, body)
    }

    if (raw) return response
    return await safeBody(response)
  } catch (error) {
    return handleError(error)
  } finally {
    clearTimeout(timer)
  }
}

/**
 * GET 请求
 */
export function get(url) {
  return request(url, { method: 'GET' })
}

/**
 * POST 表单请求（application/x-www-form-urlencoded）
 * @param {string} url
 * @param {Object} params 表单参数对象
 */
export function postForm(url, params = {}) {
  return request(url, {
    method: 'POST',
    body: toFormData(params),
  })
}



// WEBPACK FOOTER //
// ./src/api/request.js